"use client";

import React, { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { cn } from "@/lib/utils";
function HeaderInner() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  return <>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        {}
        <div className="flex-1 md:flex-none">
          <Link to="/" className="inline-block">
            <WvcLogo className="h-7 w-auto text-foreground" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          {loading ? <div className="flex gap-6">
              {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
            </div> : <NavigationMenu viewport={false}>
              <NavigationMenuList className="gap-8">
                {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                    <NavigationMenuLink asChild>
                      <Link to={item.href || "#"} className="font-default font-medium uppercase text-xs tracking-widest text-foreground hover:underline decoration-secondary decoration-2 underline-offset-8 transition-all">
                        {item.label}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>)}
              </NavigationMenuList>
            </NavigationMenu>}
        </div>

        {}
        <div className="flex items-center gap-1 md:gap-2 flex-1 md:flex-none justify-end">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-transparent hover:opacity-70">
                <Search className="size-5" />
                <span className="sr-only">Suche</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3 mt-2 bg-popover border-border rounded-lg shadow-lg">
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Lieblingsstücke finden..." />
            </PopoverContent>
          </Popover>

          <CartIcon variant="ghost" size="icon" iconSize="size-5" className="text-foreground hover:bg-transparent hover:opacity-70" />

          <Button variant="ghost" size="icon" className="md:hidden text-foreground ml-1" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            <span className="sr-only">Menü</span>
          </Button>
        </div>
      </div>

      {}
      {isOpen && !loading && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-lg border-b border-border p-4 flex flex-col gap-2 md:hidden shadow-xl">
          {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-default font-medium uppercase text-xs tracking-widest text-foreground py-4 border-b border-border/50 last:border-0 hover:text-secondary transition-colors" onClick={() => setIsOpen(false)}>
              {item.label}
            </Link>)}
        </div>}
    </>;
}
export default function PremiumNavigationBar() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="261858" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b border-transparent bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-xl border-border py-3 shadow-md" : "bg-background py-8")}>
      <MenuProvider menu_id="40">
        <HeaderInner />
      </MenuProvider>
    </section>;
}